import {
  ALERT_CRITICALITY_LABELS,
  INITIATIVE_STATUS_LABELS,
  TRAFFIC_LIGHT_LABELS,
  TREND_LABELS,
  formatDate,
  formatDateTime,
  formatDecimal,
  perspectiveDashboardSchema,
  targetPeriodLabel,
  type PerspectiveDashboard,
} from '@fluxus/shared';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { ArrowDownRight, ArrowRight, ArrowUpRight, FolderKanban, Minus } from 'lucide-react';
import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { Link, useParams, useSearchParams } from 'react-router';
import {
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { PageContainer, PageHeader } from '@/components/layout/PageHeader';
import { EmptyState, ErrorState, LoadingState } from '@/components/states/States';
import { STATUS_DOT, StatusBadge } from '@/components/status/StatusBadge';
import { NativeSelect } from '@/components/ui/select';
import { actionLink } from '@/features/meetings/CriticalAnalysis';
import { DRILL_PARAMS, drillHref, readPeriod } from '@/features/drill/drill';
import { DRILL_NUMBER, DrillBadgeLink } from '@/features/drill/DrillLinks';
import { DrillTrail } from '@/features/drill/DrillTrail';
import { ProjectPicker, useCurrentProject } from '@/features/workspace/workspace';
import { ApiError } from '@/lib/api/errors';
import { useAuth, useCan } from '@/lib/auth/session';
import { cn } from '@/lib/cn';

const pct = (v: number | null) => (v === null ? '—' : `${formatDecimal(v, 1)}%`);
const num = (v: number | null, unit: string) =>
  v === null
    ? '—'
    : unit === 'R$'
      ? `R$ ${formatDecimal(v, 2)}`
      : unit === '%'
        ? `${formatDecimal(v, 2)}%`
        : `${formatDecimal(v, 2)} ${unit}`;
const monthLabel = (year: number, month: number) => `${targetPeriodLabel('MONTH', month)}/${year}`;

/**
 * Dashboard por perspectiva (T-142b, RF-35; DEC-056): a perspectiva escolhida com a performance ponderada e a
 * tendência, os objetivos com o peso na ponderação, os KPIs, os desvios, as iniciativas e as ações em atraso.
 */
export function DashboardPerspectivaPage() {
  const { organizationId = '' } = useParams();
  const { workspace, projects, project, select } = useCurrentProject(organizationId);
  return (
    <PageContainer>
      <PageHeader
        title="Dashboard por perspectiva"
        description="Aprofunde uma perspectiva: objetivos e pesos, indicadores, tendência, desvios, iniciativas e ações em atraso."
      />
      {workspace.isPending ? (
        <LoadingState className="mt-6" rows={4} label="Carregando o projeto" />
      ) : workspace.isError ? (
        <ErrorState
          className="mt-6"
          error={workspace.error}
          onRetry={() => void workspace.refetch()}
          retrying={workspace.isFetching}
        />
      ) : !project ? (
        <EmptyState
          className="mt-6 rounded-lg border border-dashed border-border py-12"
          icon={FolderKanban}
          title="Nenhum projeto BSC em andamento"
          description="O dashboard mostra a estratégia de um projeto. Quando a empresa tiver um projeto, ele aparece aqui."
        />
      ) : (
        <div key={project.id} className="mt-4 flex flex-col gap-6">
          <ProjectPicker projects={projects} project={project} onSelect={select} />
          <Dashboard organizationId={organizationId} project={project} />
        </div>
      )}
    </PageContainer>
  );
}

function Dashboard({
  organizationId,
  project,
}: {
  organizationId: string;
  project: { id: string; horizonStart: number; horizonEnd: number };
}) {
  const { client } = useAuth();
  const ids = { perspective: useId(), year: useId(), month: useId() };
  // A perspectiva vai no endereço (?perspectiva=): o link pode ser compartilhado.
  const [params, setParams] = useSearchParams();
  const perspectiveId = params.get('perspectiva');
  // O período do link (drill-down — T-144b, DEC-058) vale quando está no horizonte; o escolhido vai ao endereço.
  const [period, setPeriodState] = useState<{ year: number; month: number } | null>(() => {
    const p = readPeriod(params);
    return p && p.year >= project.horizonStart && p.year <= project.horizonEnd ? p : null;
  });
  // Um link para a mesma tela com outro período (sem remontar) passa a valer.
  const linkYear = params.get(DRILL_PARAMS.year);
  const linkMonth = params.get(DRILL_PARAMS.month);
  useEffect(() => {
    const p = readPeriod(new URLSearchParams({ ano: linkYear ?? '', mes: linkMonth ?? '' }));
    if (!p || p.year < project.horizonStart || p.year > project.horizonEnd) return;
    setPeriodState((prev) => (prev && prev.year === p.year && prev.month === p.month ? prev : p));
  }, [linkYear, linkMonth, project.horizonStart, project.horizonEnd]);
  const setPeriod = (p: { year: number; month: number }) => {
    setPeriodState(p);
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set(DRILL_PARAMS.year, String(p.year));
        next.set(DRILL_PARAMS.month, String(p.month));
        return next;
      },
      { replace: true },
    );
  };
  const query = useQuery({
    queryKey: ['perspective-dashboard', project.id, perspectiveId, period],
    queryFn: ({ signal }) =>
      client.request(`/organizations/${organizationId}/projects/${project.id}/perspective-dashboard`, {
        query: {
          ...(perspectiveId ? { perspectiveId } : {}),
          ...(period ? { year: String(period.year), month: String(period.month) } : {}),
        },
        schema: perspectiveDashboardSchema,
        signal,
      }),
    placeholderData: keepPreviousData,
  });
  // Perspectiva do endereço que não é deste projeto (troca de projeto, link antigo ou perspectiva arquivada): sai do
  // endereço e a tela volta à primeira perspectiva do projeto.
  const missing =
    query.isError && perspectiveId !== null && query.error instanceof ApiError && query.error.status === 404;
  useEffect(() => {
    if (!missing) return;
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete('perspectiva');
        return next;
      },
      { replace: true },
    );
  }, [missing, setParams]);
  const setPerspective = (id: string) =>
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set('perspectiva', id);
        return next;
      },
      { replace: true },
    );

  // A última resposta boa: com erro numa troca de filtro, os seletores continuam (perspectivas e período).
  const lastGood = useRef<PerspectiveDashboard | null>(null);
  if (query.data) lastGood.current = query.data;

  if (query.isPending) return <LoadingState rows={6} label="Carregando o dashboard" />;
  const d = query.data ?? lastGood.current;
  const shown = period ?? (d ? { year: d.year, month: d.month } : null);
  const chosen = perspectiveId ?? d?.perspective?.id ?? '';
  const updating = query.isFetching && query.isPlaceholderData;
  const years = Array.from(
    { length: project.horizonEnd - project.horizonStart + 1 },
    (_, i) => project.horizonStart + i,
  );
  const error = (
    <ErrorState error={query.error} onRetry={() => void query.refetch()} retrying={query.isFetching} />
  );
  if (!shown || !d) return error;

  const filters = (
    <div className="flex flex-wrap items-end gap-3">
      <div className="min-w-0">
        <label htmlFor={ids.perspective} className="mb-1 block text-xs font-medium text-muted-foreground">
          Perspectiva
        </label>
        <NativeSelect id={ids.perspective} value={chosen} onChange={(e) => setPerspective(e.target.value)}>
          {d.perspectives.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name}
            </option>
          ))}
        </NativeSelect>
      </div>
      <div>
        <label htmlFor={ids.month} className="mb-1 block text-xs font-medium text-muted-foreground">
          Mês
        </label>
        <NativeSelect
          id={ids.month}
          value={shown.month}
          onChange={(e) => setPeriod({ year: shown.year, month: Number(e.target.value) })}
        >
          {Array.from({ length: 12 }, (_, i) => (
            <option key={i + 1} value={i + 1}>
              {targetPeriodLabel('MONTH', i + 1)}
            </option>
          ))}
        </NativeSelect>
      </div>
      <div>
        <label htmlFor={ids.year} className="mb-1 block text-xs font-medium text-muted-foreground">
          Ano
        </label>
        <NativeSelect
          id={ids.year}
          value={shown.year}
          onChange={(e) => setPeriod({ year: Number(e.target.value), month: shown.month })}
        >
          {years.map((y) => (
            <option key={y} value={y}>
              {y}
            </option>
          ))}
        </NativeSelect>
      </div>
      <p className="pb-2 text-xs text-muted-foreground" aria-live="polite">
        {updating
          ? `Atualizando…`
          : query.isError
            ? null
            : d.computedAt
              ? `Dados de ${monthLabel(d.year, d.month).toLowerCase()}, calculados em ${formatDateTime(d.computedAt)}`
              : `Ainda não há performance calculada em ${monthLabel(d.year, d.month).toLowerCase()}`}
      </p>
    </div>
  );
  if (query.isError) {
    return (
      <div className="flex flex-col gap-6">
        {filters}
        {error}
      </div>
    );
  }
  if (!d.perspective) {
    return (
      <EmptyState
        className="rounded-lg border border-dashed border-border py-12"
        icon={FolderKanban}
        title="O projeto ainda não tem perspectivas"
        description="As perspectivas são criadas na construção do BSC; com elas, o dashboard mostra cada uma em detalhe."
      />
    );
  }
  return (
    <div className="flex flex-col gap-6" aria-busy={query.isFetching}>
      {filters}
      <DrillTrail
        organizationId={organizationId}
        path={{ projectId: d.project.id, perspectiveId: d.perspective.id, year: d.year, month: d.month }}
      />
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,2fr)]">
        <section
          aria-labelledby="perf-perspectiva"
          className="min-w-0 rounded-lg border border-border bg-surface p-5"
        >
          <h2 id="perf-perspectiva" className="text-sm font-semibold text-muted-foreground">
            Performance ponderada — {d.perspective.name}
          </h2>
          {d.performance && d.performance.performance !== null ? (
            <>
              <p className="mt-2 flex items-center gap-3">
                {/* O número leva aos objetivos que o compõem (drill-down — T-144c). */}
                {d.objectives ? (
                  <a
                    href={`#${OBJECTIVES_ANCHOR}`}
                    className={cn('text-4xl font-extrabold tabular-nums', DRILL_NUMBER)}
                    aria-label={`Performance ponderada ${pct(d.performance.performance)}: ver os objetivos que a compõem`}
                  >
                    {pct(d.performance.performance)}
                  </a>
                ) : (
                  <span className="text-4xl font-extrabold tabular-nums">
                    {pct(d.performance.performance)}
                  </span>
                )}
                <StatusBadge status={d.performance.light} />
              </p>
              <Trend value={d.performance.trend} />
              <p className="mt-2 text-sm text-muted-foreground">
                Acumulado do ano:{' '}
                {d.objectives ? (
                  <a
                    href={`#${OBJECTIVES_ANCHOR}`}
                    className={cn('font-semibold text-foreground tabular-nums', DRILL_NUMBER)}
                    aria-label={`Acumulado do ano ${pct(d.performance.ytdPerformance)}: ver os objetivos que o compõem`}
                  >
                    {pct(d.performance.ytdPerformance)}
                  </a>
                ) : (
                  <span className="font-semibold text-foreground tabular-nums">
                    {pct(d.performance.ytdPerformance)}
                  </span>
                )}
              </p>
              <TrendDirection d={d} />
            </>
          ) : (
            <p className="mt-2 text-sm text-muted-foreground">
              Sem performance da perspectiva neste mês: ela aparece quando há metas e resultados lançados.
            </p>
          )}
        </section>
        <TrendChart d={d} />
      </div>
      <Objectives d={d} organizationId={organizationId} />
      <Kpis d={d} organizationId={organizationId} />
      <Deviations d={d} organizationId={organizationId} />
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Initiatives d={d} organizationId={organizationId} />
        <LateActions d={d} organizationId={organizationId} />
      </div>
    </div>
  );
}

function Trend({ value }: { value: number | null }) {
  if (value === null)
    return <p className="mt-1 text-sm text-muted-foreground">Sem o mês anterior para comparar.</p>;
  const Icon = value > 0 ? ArrowUpRight : value < 0 ? ArrowDownRight : Minus;
  return (
    <p
      className={cn(
        'mt-1 flex items-center gap-1 text-sm tabular-nums',
        value > 0 ? 'text-status-green-fg' : value < 0 ? 'text-status-red-fg' : 'text-muted-foreground',
      )}
    >
      <Icon className="size-4" aria-hidden />
      {`${value > 0 ? '+' : ''}${formatDecimal(value, 1)} p.p. vs mês anterior`}
    </p>
  );
}

function TrendDirection({ d }: { d: PerspectiveDashboard }) {
  const t = d.trend.direction;
  if (!t) return null;
  return (
    <p className="mt-2 text-sm">
      Tendência: <span className="font-semibold">{TREND_LABELS[t.direction]}</span>{' '}
      <span className="text-muted-foreground">
        (de {pct(t.first.performance)} em {monthLabel(t.first.year, t.first.month).toLowerCase()} para{' '}
        {pct(t.last.performance)} em {monthLabel(t.last.year, t.last.month).toLowerCase()}, {t.months} meses)
      </span>
    </p>
  );
}

const OBJECTIVES_ANCHOR = 'objetivos-da-perspectiva';

function Section({
  title,
  count,
  anchor,
  children,
}: {
  title: string;
  count?: number;
  /** Âncora para os números de cima levarem até aqui. */
  anchor?: string;
  children: ReactNode;
}) {
  const id = useId();
  return (
    <section
      id={anchor}
      aria-labelledby={id}
      className="flex min-w-0 flex-col gap-2 rounded-lg border border-border bg-surface p-4"
    >
      <h2 id={id} className="flex items-center gap-2 text-sm font-bold">
        {title}
        {count !== undefined ? (
          <span className="ml-auto rounded-full bg-muted px-2 py-0.5 text-xs tabular-nums">{count}</span>
        ) : null}
      </h2>
      {children}
    </section>
  );
}

function ItemLink({ to, children }: { to: string | null; children: ReactNode }) {
  return to ? (
    <Link to={to} className="flex gap-2 rounded-md px-1 py-2 hover:bg-muted/50">
      {children}
      <ArrowRight className="mt-1 size-4 shrink-0 text-muted-foreground" aria-hidden />
    </Link>
  ) : (
    <div className="flex gap-2 px-1 py-2">{children}</div>
  );
}

function Objectives({ d, organizationId }: { d: PerspectiveDashboard; organizationId: string }) {
  if (!d.objectives) return null;
  return (
    <Section title="Objetivos" count={d.objectives.length} anchor={OBJECTIVES_ANCHOR}>
      {d.objectives.length ? (
        <ul aria-label="Objetivos da perspectiva" className="flex flex-col divide-y divide-border">
          {d.objectives.map((o) => (
            <li key={o.id}>
              <ItemLink
                to={drillHref.objective(organizationId, {
                  projectId: d.project.id,
                  objectiveId: o.id,
                  year: d.year,
                  month: d.month,
                })}
              >
                <span
                  className={cn('mt-1.5 size-2 shrink-0 rounded-full', STATUS_DOT[o.light])}
                  aria-hidden
                />
                <span className="min-w-0 flex-1">
                  <span className="block break-words font-medium">{o.name}</span>
                  <span className="block text-xs text-muted-foreground">
                    Peso {formatDecimal(o.weight, 2)}%{o.owner ? ` · ${o.owner}` : ''}
                    {o.trend !== null ? ` · ${o.trend > 0 ? '+' : ''}${formatDecimal(o.trend, 1)} p.p.` : ''}
                    {o.source === 'CHILDREN'
                      ? ' · consolidado dos desdobramentos (não entra na média da perspectiva)'
                      : ''}
                  </span>
                </span>
                <span className="shrink-0 text-right">
                  <span className="block font-bold tabular-nums">{pct(o.performance)}</span>
                  <span className="block text-xs text-muted-foreground">{TRAFFIC_LIGHT_LABELS[o.light]}</span>
                </span>
              </ItemLink>
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-sm text-muted-foreground">Nenhum objetivo desta perspectiva no seu escopo.</p>
      )}
    </Section>
  );
}

function Kpis({ d, organizationId }: { d: PerspectiveDashboard; organizationId: string }) {
  const can = useCan();
  if (!d.kpis) return null;
  const seesHistory = can('metas-resultados', 'view');
  return (
    <Section title="KPIs relacionados" count={d.kpis.length}>
      {d.kpis.length ? (
        <div className="overflow-x-auto" role="region" aria-label="KPIs da perspectiva" tabIndex={0}>
          <table className="w-full text-sm">
            <caption className="sr-only">KPIs da perspectiva no mês</caption>
            <thead className="text-xs text-muted-foreground">
              <tr>
                <th scope="col" className="py-1 text-left font-medium">
                  Indicador
                </th>
                <th scope="col" className="py-1 pl-3 text-right font-medium">
                  Realizado
                </th>
                <th scope="col" className="py-1 pl-3 text-right font-medium">
                  Meta
                </th>
                <th scope="col" className="py-1 pl-3 text-left font-medium">
                  Performance
                </th>
              </tr>
            </thead>
            <tbody>
              {d.kpis.map((k) => (
                <tr key={k.id} className="border-t border-border">
                  <th scope="row" className="py-2 pr-2 text-left font-medium break-words">
                    {seesHistory ? (
                      <Link
                        className="text-brand-strong underline-offset-2 hover:underline dark:text-brand"
                        to={drillHref.kpi(organizationId, {
                          projectId: d.project.id,
                          kpiId: k.id,
                          year: d.year,
                          month: d.month,
                        })}
                      >
                        {k.code} · {k.name}
                      </Link>
                    ) : (
                      `${k.code} · ${k.name}`
                    )}
                  </th>
                  <td className="py-2 pl-3 text-right tabular-nums">{num(k.actual, k.unitLabel)}</td>
                  <td className="py-2 pl-3 text-right tabular-nums">{num(k.target, k.unitLabel)}</td>
                  <td className="py-2 pl-3">
                    {seesHistory ? (
                      // O número do KPI leva às unidades (o nível de baixo).
                      <DrillBadgeLink
                        to={drillHref.unit(organizationId, {
                          projectId: d.project.id,
                          kpiId: k.id,
                          year: d.year,
                          month: d.month,
                        })}
                        label={`${k.code} ${k.performance === null ? 'sem dados' : pct(k.performance)}: ver por unidade`}
                      >
                        <StatusBadge
                          status={k.light}
                          label={k.performance === null ? 'Sem dados' : pct(k.performance)}
                        />
                      </DrillBadgeLink>
                    ) : (
                      <StatusBadge
                        status={k.light}
                        label={k.performance === null ? 'Sem dados' : pct(k.performance)}
                      />
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <p className="text-sm text-muted-foreground">Nenhum KPI desta perspectiva no seu escopo.</p>
      )}
    </Section>
  );
}

function Deviations({ d, organizationId }: { d: PerspectiveDashboard; organizationId: string }) {
  const can = useCan();
  if (!d.deviations && !d.reportedDeviations) return null;
  const seesHistory = can('metas-resultados', 'view');
  return (
    <Section title="Desvios" count={(d.deviations?.length ?? 0) + (d.reportedDeviations?.total ?? 0)}>
      {d.deviations ? (
        d.deviations.length ? (
          <ul aria-label="KPIs fora da meta" className="flex flex-col divide-y divide-border">
            {d.deviations.map((k) => (
              <li key={k.kpiId}>
                <ItemLink
                  to={
                    seesHistory
                      ? drillHref.kpi(organizationId, {
                          projectId: d.project.id,
                          kpiId: k.kpiId,
                          year: d.year,
                          month: d.month,
                        })
                      : null
                  }
                >
                  <span
                    className={cn('mt-1.5 size-2 shrink-0 rounded-full', STATUS_DOT[k.light])}
                    aria-hidden
                  />
                  <span className="min-w-0 flex-1">
                    <span className="block break-words font-medium">
                      {k.code} · {k.name}
                    </span>
                    <span className="block text-xs text-muted-foreground">
                      Realizado {num(k.actual, k.unitLabel)} · meta {num(k.target, k.unitLabel)} · diferença{' '}
                      {num(k.gap, k.unitLabel)}
                    </span>
                  </span>
                  <span className="shrink-0 font-bold tabular-nums">{pct(k.performance)}</span>
                </ItemLink>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-muted-foreground">Nenhum KPI fora da meta no mês.</p>
        )
      ) : null}
      {d.reportedDeviations ? (
        <div className="flex flex-col gap-1 border-t border-border pt-2">
          <h3 className="text-xs font-semibold text-muted-foreground">Desvios informados em aberto</h3>
          {d.reportedDeviations.items.length ? (
            <ul aria-label="Desvios informados" className="flex flex-col divide-y divide-border">
              {d.reportedDeviations.items.map((a) => (
                <li key={a.id}>
                  <ItemLink
                    to={`/empresas/${organizationId}/gestao/alertas?projeto=${d.project.id}&alerta=${a.id}`}
                  >
                    <span className="min-w-0 flex-1">
                      <span className="block break-words font-medium">{a.title}</span>
                      <span className="block break-words text-xs text-muted-foreground">
                        {ALERT_CRITICALITY_LABELS[a.criticality]} · {formatDate(a.createdAt.slice(0, 10))} ·{' '}
                        {a.message}
                      </span>
                    </span>
                  </ItemLink>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-muted-foreground">Nenhum desvio informado em aberto.</p>
          )}
          {d.reportedDeviations.total > d.reportedDeviations.items.length ? (
            <p className="text-xs text-muted-foreground">
              e mais {d.reportedDeviations.total - d.reportedDeviations.items.length}
            </p>
          ) : null}
        </div>
      ) : null}
    </Section>
  );
}

function Initiatives({ d, organizationId }: { d: PerspectiveDashboard; organizationId: string }) {
  const b = d.initiatives;
  if (!b) return null;
  return (
    <Section title="Iniciativas" count={b.total}>
      {b.items.length ? (
        <ul aria-label="Iniciativas da perspectiva" className="flex flex-col divide-y divide-border">
          {b.items.map((i) => (
            <li key={i.id}>
              <ItemLink
                to={`/empresas/${organizationId}/execucao/iniciativas?projeto=${d.project.id}&iniciativa=${i.id}`}
              >
                <span className="min-w-0 flex-1">
                  <span className="block break-words font-medium">{i.name}</span>
                  <span className="block text-xs text-muted-foreground">
                    {i.objective} · {INITIATIVE_STATUS_LABELS[i.status]} · {formatDecimal(i.progress, 0)}%
                    concluído
                    {i.late && i.endDate ? ` · atrasada (fim previsto ${formatDate(i.endDate)})` : ''}
                  </span>
                </span>
              </ItemLink>
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-sm text-muted-foreground">
          Nenhuma iniciativa aberta nos objetivos desta perspectiva.
        </p>
      )}
      {b.total > b.items.length ? (
        <p className="text-xs text-muted-foreground">e mais {b.total - b.items.length}</p>
      ) : null}
    </Section>
  );
}

function LateActions({ d, organizationId }: { d: PerspectiveDashboard; organizationId: string }) {
  const b = d.lateActions;
  if (!b) return null;
  return (
    <Section title="Ações em atraso" count={b.total}>
      {b.items.length ? (
        <ul aria-label="Ações em atraso da perspectiva" className="flex flex-col divide-y divide-border">
          {b.items.map((a) => (
            <li key={a.id}>
              <ItemLink to={actionLink(organizationId, d.project.id, a.planId, a.id)}>
                <span className="mt-1.5 size-2 shrink-0 rounded-full bg-status-red-solid" aria-hidden />
                <span className="min-w-0 flex-1">
                  <span className="block break-words font-medium">{a.title}</span>
                  <span className="block text-xs text-muted-foreground">
                    {a.planName} · prazo {formatDate(a.dueDate)}
                    {a.responsibleName ? ` · ${a.responsibleName}` : ''}
                  </span>
                </span>
              </ItemLink>
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-sm text-muted-foreground">Nenhuma ação em atraso ligada a esta perspectiva.</p>
      )}
      {b.total > b.items.length ? (
        <p className="text-xs text-muted-foreground">e mais {b.total - b.items.length}</p>
      ) : null}
    </Section>
  );
}

function TrendChart({ d }: { d: PerspectiveDashboard }) {
  const points = d.trend.months.map((m) => ({
    name: targetPeriodLabel('MONTH', m.month),
    mes: m.performance,
    acumulado: m.ytdPerformance,
  }));
  const tick = { fontSize: 12, fill: 'var(--muted-foreground)' };
  const summary = points
    .map(
      (p) =>
        `${p.name}: ${p.mes === null ? 'sem dados' : pct(p.mes)}${p.acumulado === null ? '' : ` (acumulado ${pct(p.acumulado)})`}`,
    )
    .join('; ');
  return (
    <section
      aria-labelledby="tendencia-perspectiva"
      className="min-w-0 rounded-lg border border-border bg-surface p-4"
    >
      <h2 id="tendencia-perspectiva" className="text-sm font-bold">
        Tendência — {d.year}
      </h2>
      {points.some((p) => p.mes !== null) ? (
        <div
          className="mt-3 h-56 w-full"
          role="img"
          aria-label={`Performance de ${d.perspective?.name} em ${d.year} — ${summary}.`}
        >
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={points} margin={{ top: 8, right: 8, bottom: 8, left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
              <XAxis dataKey="name" tick={tick} />
              <YAxis tick={tick} width={48} tickFormatter={(v: number) => `${v}%`} />
              <Tooltip formatter={(v) => (typeof v === 'number' ? pct(v) : '—')} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Line
                type="monotone"
                dataKey="mes"
                name="Mês"
                stroke="var(--brand)"
                strokeWidth={2.5}
                isAnimationActive={false}
                connectNulls={false}
              />
              <Line
                type="monotone"
                dataKey="acumulado"
                name="Acumulado do ano"
                stroke="var(--status-blue-solid)"
                strokeDasharray="5 4"
                isAnimationActive={false}
                connectNulls={false}
              />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <p className="mt-3 text-sm text-muted-foreground">Sem performance desta perspectiva em {d.year}.</p>
      )}
    </section>
  );
}
